// POS front üçün yüngül çoxdilli (i18n) kontekst — kitabxanasız.
//
// Dillər: en (default, ilk), az, ru, tr. Seçim localStorage-da saxlanır.
// İstifadə:
//   const { t, lang, setLang } = useI18n();
//   <span>{t("printers.title")}</span>
//   t("catalog.priceChanged", { name, from, to })   // {placeholder} əvəzlənir
//
// Tərcümələr: src/i18n/translations.ts (açar → { en, az, ru, tr }).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

import { translations, type TransKey } from "./translations";

export type Lang = "en" | "az" | "ru" | "tr";

export const LANGUAGES: Array<{ code: Lang; label: string; short: string }> = [
  { code: "en", label: "English", short: "EN" },
  { code: "az", label: "Azərbaycan", short: "AZ" },
  { code: "ru", label: "Русский", short: "RU" },
  { code: "tr", label: "Türkçe", short: "TR" },
];

const STORAGE_KEY = "pos_lang";
const DEFAULT_LANG: Lang = "en";

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "en" || v === "az" || v === "ru" || v === "tr") return v;
  } catch {
    /* noop */
  }
  return DEFAULT_LANG;
}

type Vars = Record<string, string | number>;

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TransKey | string, vars?: Vars) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_m, k) =>
    vars[k] !== undefined ? String(vars[k]) : `{${k}}`,
  );
}

/**
 * React-dən kənar tərcümə (məs. catalogNotify, axios interceptor). Cari dili
 * localStorage-dan oxuyur — komponent kontekstindən asılı deyil.
 */
export function tStatic(key: TransKey | string, vars?: Vars): string {
  const lang = readStoredLang();
  const entry = (translations as Record<string, Partial<Record<Lang, string>>>)[key];
  if (!entry) return interpolate(String(key), vars);
  const text = entry[lang] ?? entry.en ?? Object.values(entry)[0] ?? String(key);
  return interpolate(text, vars);
}

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readStoredLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* noop */
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);

  const t = useCallback(
    (key: TransKey | string, vars?: Vars): string => {
      const entry = (translations as Record<string, Partial<Record<Lang, string>>>)[key];
      if (!entry) return interpolate(String(key), vars); // açar tapılmadı → açarın özü
      const text = entry[lang] ?? entry.en ?? Object.values(entry)[0] ?? String(key);
      return interpolate(text, vars);
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Provider yoxdursa (məs. test) — təhlükəsiz fallback: en/açar.
    return {
      lang: DEFAULT_LANG,
      setLang: () => {},
      t: (key: TransKey | string, vars?: Vars) => {
        const entry = (translations as Record<string, Partial<Record<Lang, string>>>)[key];
        const text = entry?.en ?? String(key);
        return interpolate(text, vars);
      },
    };
  }
  return ctx;
}
